/**
 * ChartMind Admin Dashboard
 * 
 * Main dashboard for ChartMind administrators.
 * Features:
 * - Facility/Organization settings
 * - Designee management
 * - Custom prompt editing (Position 3)
 * - Invite link management
 * - Named prompt templates (Position 3 prompt collections with QR sharing)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { contentCardSx } from '../../../../../styles/standaloneLayoutTokens';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import TuneIcon from '@mui/icons-material/Tune';
import LinkIcon from '@mui/icons-material/Link';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import { useAuth } from '@healthdesk/shared-hooks';
import { chartmindAdminService, CHARTMIND_CARD_LIST } from '../../../../../services/chartmind';
import { queryRegistryService } from '../../../../../services/llm';
import PromptEditor from './PromptEditor';
import DesigneeManager from './DesigneeManager';
import InviteLinkManager from './InviteLinkManager';
import FacilitySettings from './FacilitySettings';
import PromptTemplateList from './PromptTemplateList';
import PromptTemplateEditor from './PromptTemplateEditor';

const ChartmindAdminDashboard = () => {
  const { user, userData } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState(0);
  const [adminData, setAdminData] = useState(null);
  const [basePrompts, setBasePrompts] = useState({});
  const [templates, setTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null); // When editing a specific template
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Load admin data
  const loadAdminData = useCallback(async () => {
    if (!user?.uid) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Get or create admin record
      const admin = await chartmindAdminService.getOrCreateAdmin(user.uid, {
        email: user.email,
        name: userData?.name || user.displayName,
      });
      setAdminData(admin);
      
      // Load base prompts (Position 1 + 2) for each card
      const prompts = {};
      for (const cardId of CHARTMIND_CARD_LIST) {
        try {
          const baseQuery = await queryRegistryService.getQuery(`chartmind-${cardId}`);
          prompts[cardId] = baseQuery?.prompt || '';
        } catch (err) {
          console.warn(`Failed to load base prompt for ${cardId}:`, err);
          prompts[cardId] = '';
        }
      }
      setBasePrompts(prompts);

      // Load templates
      try {
        const tpls = await chartmindAdminService.getTemplates(admin.id);
        setTemplates(tpls);
      } catch (err) {
        console.warn('[ChartmindAdminDashboard] Failed to load templates:', err);
      }
      
    } catch (err) {
      console.error('[ChartmindAdminDashboard] Error loading data:', err);
      setError('Failed to load admin data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [user, userData]);

  // Reload templates
  const loadTemplates = useCallback(async () => {
    if (!adminData?.id) return;
    try {
      const tpls = await chartmindAdminService.getTemplates(adminData.id);
      setTemplates(tpls);
    } catch (err) {
      console.warn('[ChartmindAdminDashboard] Failed to reload templates:', err);
    }
  }, [adminData?.id]);

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  // Handle prompt save
  const handleSavePrompt = useCallback(async (cardId, config) => {
    if (!adminData?.id) return;
    
    setIsSaving(true);
    try {
      await chartmindAdminService.updateCustomPrompt(
        adminData.id,
        cardId,
        config,
        user.email
      );
      
      // Refresh admin data
      const updated = await chartmindAdminService.getAdminById(adminData.id);
      setAdminData(updated);
      setSuccessMessage('Prompt saved successfully!');
    } catch (err) {
      console.error('[ChartmindAdminDashboard] Error saving prompt:', err);
      setError('Failed to save prompt. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [adminData, user]);

  // Handle prompt toggle
  const handleTogglePrompt = useCallback(async (cardId, enabled) => {
    if (!adminData?.id) return;
    
    try {
      await chartmindAdminService.toggleCustomPrompt(adminData.id, cardId, enabled);
      
      // Refresh admin data
      const updated = await chartmindAdminService.getAdminById(adminData.id);
      setAdminData(updated);
      setSuccessMessage(`Prompt ${enabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      console.error('[ChartmindAdminDashboard] Error toggling prompt:', err);
      setError('Failed to toggle prompt. Please try again.');
    }
  }, [adminData]);

  // Handle facility settings update
  const handleUpdateFacility = useCallback(async (settings) => {
    if (!adminData?.id) return;
    
    setIsSaving(true);
    try {
      await chartmindAdminService.updateFacilitySettings(adminData.id, settings);
      
      // Refresh admin data
      const updated = await chartmindAdminService.getAdminById(adminData.id);
      setAdminData(updated);
      setSuccessMessage('Settings updated successfully!');
    } catch (err) {
      console.error('[ChartmindAdminDashboard] Error updating settings:', err);
      setError('Failed to update settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [adminData]);

  // Handle designee changes
  const handleDesigneeChange = useCallback(async () => {
    // Refresh admin data after designee changes
    if (!adminData?.id) return;
    
    const updated = await chartmindAdminService.getAdminById(adminData.id);
    setAdminData(updated);
  }, [adminData]);

  // Tab panels
  const tabs = [
    { label: 'Settings', icon: <SettingsIcon /> },
    { label: 'Team', icon: <PeopleIcon /> },
    { label: 'Prompts', icon: <TuneIcon /> },
    { label: 'Note Types', icon: <LibraryBooksIcon /> },
    { label: 'Invite Links', icon: <LinkIcon /> },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !adminData) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          ChartMind Admin
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your organization settings, team members, and custom prompts.
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper elevation={0} sx={{ ...contentCardSx, mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="fullWidth"
        >
          {tabs.map((tab, index) => (
            <Tab 
              key={index} 
              icon={tab.icon} 
              label={tab.label}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box>
        {/* Settings Tab */}
        {activeTab === 0 && (
          <FacilitySettings
            facilityName={adminData?.facilityName || ''}
            organizationName={adminData?.organizationName || ''}
            outboundCallerId={adminData?.outboundCallerId || ''}
            onSave={handleUpdateFacility}
            isSaving={isSaving}
          />
        )}

        {/* Team Tab */}
        {activeTab === 1 && (
          <DesigneeManager
            adminId={adminData?.id}
            designees={adminData?.designeeEmails || []}
            pendingDesignees={adminData?.pendingDesignees || []}
            onDesigneeChange={handleDesigneeChange}
          />
        )}

        {/* Prompts Tab */}
        {activeTab === 2 && (
          <PromptEditor
            adminId={adminData?.id}
            customPrompts={adminData?.customPrompts || {}}
            basePrompts={basePrompts}
            onSavePrompt={handleSavePrompt}
            onTogglePrompt={handleTogglePrompt}
            isSaving={isSaving}
          />
        )}

        {/* Note Types Tab */}
        {activeTab === 3 && (
          editingTemplate ? (
            <PromptTemplateEditor
              adminId={adminData?.id}
              template={editingTemplate}
              userEmail={user?.email}
              onBack={() => {
                setEditingTemplate(null);
                loadTemplates();
              }}
              onSaved={loadTemplates}
            />
          ) : (
            <PromptTemplateList
              adminId={adminData?.id}
              templates={templates}
              onTemplatesChange={loadTemplates}
              onEditTemplate={(tpl) => setEditingTemplate(tpl)}
            />
          )
        )}

        {/* Invite Links Tab */}
        {activeTab === 4 && (
          <InviteLinkManager
            adminId={adminData?.id}
            inviteLinks={adminData?.inviteLinks || []}
            onInviteLinkChange={handleDesigneeChange}
          />
        )}
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />
    </Container>
  );
};

export default ChartmindAdminDashboard;
